import React, { useCallback } from 'react';
import { Handle, Position } from 'reactflow';
import type { ToolCallDagData } from '../types';
import { ToolCallPill } from '../../ToolCallPill';
import { usePersonaChatUI } from '../../../../contexts/PersonaChatUIContext';
import { useChatDagRuntime, useDagNodeOpacity } from '../../../../contexts/ChatDagRuntimeContext';
import HoverCard from '../../../common/HoverCard';
import DagNodeHoverContent from './DagNodeHoverContent';
import { useTheme } from '../../../../contexts/ThemeContext';
import { HOVER_LIFT_CLASS } from '../chatDagTokens';
import { CopyChip } from '../CopyChip';

// Per-tool key-arg preview — the one snippet that answers "what was
// this call actually doing?" at a glance. Always tries to return
// SOMETHING so every tool node has an args line; the hover provides
// the structured full-args view in any case.
function extractKeyArgPreview(toolName: string, args: any): string | null {
  if (!args || typeof args !== 'object') return null;
  const truncate = (s: string, max = 80) =>
    s.length > max ? `${s.slice(0, max)}…` : s;
  switch (toolName) {
    case 'consult_peers': {
      // Parent node IS the source of truth for the shared prompt.
      // Peer fans show per-peer match info; the prompt is shared.
      const n =
        Array.isArray(args.peer_ids) ? args.peer_ids.length : null;
      const prompt = typeof args.prompt === 'string' ? args.prompt : null;
      const peerStr = n ? `${n} peer${n === 1 ? '' : 's'}` : null;
      const promptStr = prompt ? `"${truncate(prompt, 60)}"` : null;
      return [peerStr, promptStr].filter(Boolean).join(' · ') || null;
    }
    case 'search':
    case 'skills_query':
      return typeof args.query === 'string' ? `"${truncate(args.query)}"` : null;
    case 'tool_docs': {
      // Two shapes: tool_name (single doc lookup) OR query (search docs).
      if (typeof args.tool_name === 'string') return args.tool_name;
      if (typeof args.query === 'string') return `"${truncate(args.query)}"`;
      return null;
    }
    case 'roster': {
      const action = typeof args.action === 'string' ? args.action : null;
      const ids = Array.isArray(args.peer_ids) ? args.peer_ids.length : null;
      if (action) return ids ? `${action} · ${ids} id${ids === 1 ? '' : 's'}` : action;
      return ids ? `${ids} ids` : null;
    }
    case 'create_artifacts':
      return Array.isArray(args.artifacts)
        ? `${args.artifacts.length} artifact${args.artifacts.length === 1 ? '' : 's'}`
        : null;
    case 'generate_mermaid_diagram':
      return typeof args.diagram_type === 'string' ? args.diagram_type : null;
    case 'document':
    case 'memory':
    case 'thread':
    case 'session':
      return typeof args.action === 'string' ? `${args.action}` : null;
    case 'event_timeline_query': {
      const parts: string[] = [];
      if (args.category) parts.push(`${args.category}`);
      if (args.since_days) parts.push(`${args.since_days}d`);
      if (args.limit) parts.push(`limit ${args.limit}`);
      return parts.length ? parts.join(' · ') : null;
    }
    case 'attention_items':
    case 'suggestions_read':
    case 'wins_read':
      return typeof args.limit === 'number' ? `limit ${args.limit}` : null;
    case 'artifactory_manifest_query':
      return typeof args.project_id === 'string'
        ? `project ${args.project_id.slice(0, 12)}…`
        : null;
    case 'handoff_note_generate':
      return typeof args.scope === 'string' ? args.scope : null;
    default:
      break;
  }
  // Generic fallback — pick the first string/number/array-valued arg.
  // Guarantees the args line shows something for any tool, even if
  // the special-cased extractor returned null. The hover always has
  // the full structured args either way.
  for (const [k, v] of Object.entries(args)) {
    if (typeof v === 'string' && v.length > 0) {
      return `${k}: ${truncate(v, 60)}`;
    }
    if (typeof v === 'number') {
      return `${k}: ${v}`;
    }
    if (Array.isArray(v)) {
      return `${k}: ${v.length}`;
    }
  }
  // Truly empty args dict — tell the operator the hover has more
  // (which for a no-arg call is just "no arguments" but at least
  // they know to look there).
  return Object.keys(args).length === 0 ? null : 'hover for details';
}

// Wraps the existing ToolCallPill so the DAG inherits the full
// palette + expand/collapse anatomy Task 28 built — one tool-call
// UI, three surfaces (bubble, panel, DAG).

export const ToolCallDagNodeCompact: React.FC<{ data: ToolCallDagData; selected?: boolean }> = ({
  data,
  selected,
}) => {
  const { darkMode } = useTheme();
  const { focusToolCall, focusBubble, toggleFanExpansion } = usePersonaChatUI();
  const { collapsedFanIds } = useChatDagRuntime();
  const tcId = data.call.identity?.tool_call_id ?? data.call.tool_call_id ?? '';
  const messageId = data.messageId;
  const toolName = data.call.identity?.tool_name ?? data.call.tool_name ?? '';
  const argPreview = extractKeyArgPreview(toolName, (data.call as any).arguments);
  // Only consult_peers has a fan worth collapsing; other tools are
  // leaves. Show the collapse toggle only when the fan is currently
  // expanded (chip has its own expand click when collapsed).
  const hasCollapsibleFan = toolName === 'consult_peers' && !!tcId;
  const isFanCollapsed = hasCollapsibleFan && collapsedFanIds.has(tcId);
  const showCollapseBtn = hasCollapsibleFan && !isFanCollapsed;
  const onCollapseClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (tcId) toggleFanExpansion(tcId);
    },
    [toggleFanExpansion, tcId],
  );

  // Double-click a DAG node to scroll + flash the corresponding row
  // in the Tools Panel. Single-click still drives ToolCallPill's own
  // expand/collapse via its internal button.
  const onDoubleClick = useCallback(() => {
    if (tcId) focusToolCall(tcId);
  }, [focusToolCall, tcId]);

  // v0.3 spine: single-click on the pill body (NOT on the internal
  // expand button) scrolls the owning bubble into view.
  const onClick = useCallback(
    (e: React.MouseEvent) => {
      if (!messageId) return;
      if ((e.target as Element).closest('button, a, input, [role="button"]')) {
        return; // let the pill's own button handle it
      }
      e.stopPropagation();
      focusBubble(messageId);
    },
    [focusBubble, messageId],
  );

  const runState = data.runState ?? 'completed';
  const running = runState === 'running';
  const opacityStyle = useDagNodeOpacity(data.turnKey, running);

  return (
    <HoverCard
      maxWidth={380}
      content={<DagNodeHoverContent data={data} />}
    >
    <div
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      className={`group relative nodrag w-[280px] rounded-md cursor-pointer ${HOVER_LIFT_CLASS} ${
        selected ? 'ring-2 ring-emerald-500' : ''
      } ${running ? 'chatdag-running-ring' : ''}`}
      title="Click → scroll to bubble · Double-click → Tools panel"
      style={opacityStyle}
    >
      {/* Branch-in from the parent round on the LEFT — matches v0.3's
          rightward tool fan. v0.2 dagre routing used a top handle but
          v0.2 is deleted in Task 17; a left target suits v0.3. */}
      <Handle
        type="target"
        position={Position.Left}
        id="l"
        className="!opacity-0"
      />
      {/* forceCollapsed keeps the pill header-only inside dagre's
          fixed slot — the expanded view is reached via double-click
          → Panel scroll + flash (see onDoubleClick above). DX triad
          called this out as a fixed-layout trap in v0.1. */}
      <ToolCallPill call={data.call} forceCollapsed />
      {(data.call as any).arguments && (
        <CopyChip
          text={(() => {
            try {
              return JSON.stringify((data.call as any).arguments, null, 2);
            } catch {
              return String((data.call as any).arguments ?? '');
            }
          })()}
          label="Copy args as JSON"
          // Top-right is reserved for the "→ Panel" discovery chip,
          // bottom-right for the consult_peers fan-collapse button —
          // CopyChip lands top-LEFT to stay out of both.
          className="top-1 left-1"
        />
      )}
      {/* Args summary line — mirrors PeerNodeCompact's "asks:" treatment.
          Per-tool extractor picks the most semantic arg (query, action,
          peer count, etc.). consult_peers returns null because its
          prompt is shown on each peer fan; rendering it here would be
          redundant. */}
      {argPreview && (
        <div
          className={`text-[10px] italic px-2 mt-0.5 truncate ${
            darkMode ? 'text-emerald-300/70' : 'text-emerald-700/70'
          }`}
          title={argPreview}
        >
          <span className="opacity-60">args:</span> {argPreview}
        </div>
      )}
      {/* Discovery affordance — chip in the corner that telegraphs
          the double-click behavior instead of hiding it behind a
          title attribute. Appears on hover. */}
      <div
        className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 pointer-events-none text-[9px] font-mono uppercase tracking-wide text-emerald-400 bg-gray-900/70 px-1 py-0.5 rounded"
        aria-hidden
      >
        → Panel
      </div>
      {/* Collapse fan toggle — only for consult_peers with an expanded
          fan. Clicking folds the peers into a FanCollapsedChip stand-
          in (user-opt-in density control). Positioned bottom-right,
          hover-revealed so it doesn't clutter the default view. */}
      {showCollapseBtn && (
        <button
          type="button"
          onClick={onCollapseClick}
          title="Collapse peer fan into a stack"
          className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 text-[9px] font-mono uppercase tracking-wide text-amber-300 bg-gray-900/80 hover:bg-gray-900 hover:text-amber-200 px-1.5 py-0.5 rounded border border-amber-500/30 transition-colors z-10"
        >
          ▸ collapse
        </button>
      )}
      {/* Fan-out to peers / chip on the RIGHT (only consult_peers
          uses this; other tools are leaves and the handle is unused). */}
      <Handle
        type="source"
        position={Position.Right}
        id="r"
        className="!opacity-0"
      />
    </div>
    </HoverCard>
  );
};

export default ToolCallDagNodeCompact;
